import { useFeatureGate } from '@statsig/react-bindings';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { PushDrawer, PushDrawerProps, SettingsDrawer } from '../components';
import { useDrawerButtonRefs } from '../hooks/useDrawerButtonRefs';
import { currentConversationSidAtom } from '../session';
import { convertAppointmentDrawerToSeedlingTelehealthGate } from '../statsig/gates';
import { isVirtualBackgroundSupportedAtom } from '../video-sdk/core/atoms';
import { ChatDrawerWrapper as ChatDrawer } from './chat';
import { AllClientsChatDrawerWrapper as AllClientsChatDrawer } from './chat/AllClientsChatDrawer';
import { ClientInformationDrawerWrapper as ClientInformationDrawer } from './client-information/ClientInformationDrawer';
import { ClientInformationFormResponseDrawerWrapper as ClientInformationFormResponseDrawer } from './client-information/ClientInformationFormResponseDrawer';
import { ClientInformationProgressNotesDrawerWrapper as ClientInformationNotesDrawer } from './client-information/ClientInformationProgressNotesDrawer';
import { PendingFormsCompletedDrawer } from './client-information/pending-forms/PendingFormsCompletedDrawer';
import { PendingFormsDrawer } from './client-information/pending-forms/PendingFormsDrawer';
import { TreatmentPlansContentDrawerWrapper as TreatmentPlansContentDrawer } from './client-information/treatment-tab/TreatmentPlansContent';
import { ClientsDrawerWrapper as ClientsDrawer } from './clients-drawer';
import { CSSRSFormWrapper as CSSRSForm } from './measures/CSSRSForm';
import { MeasureResponsesDrawer } from './measures/MeasureResponsesDrawer';
import { PendingMeasuresDrawerWrapper as PendingMeasuresDrawer } from './measures/PendingMeasuresDrawer';
import { EditAppointmentDrawerWrapper as EditAppointmentDrawer } from './schedule-appointment/EditAppointmentDrawer';
import { SchedulingAppointmentDrawerWrapper } from './schedule-appointment/ScheduleAppointmentDrawerWrapper';
import { AddAppointmentDrawerWrapper as AddAppointmentDrawer } from './schedule-appointment/add-appointment-drawer/AddAppointmentDrawer';
import { getSupportedTabs } from './settingsTabMap';
import { ProviderDrawerState, drawerStateAtom, selectedBundleAtom, selectedMeasureBundleItemIdAtom } from './state';

function DrawerContent({
  drawerState,
  isVirtualBackgroundSupported,
}: {
  drawerState: ProviderDrawerState | null;
  isVirtualBackgroundSupported?: boolean;
}) {
  const convertAppointmentDrawerToSeedlingTelehealth = useFeatureGate(convertAppointmentDrawerToSeedlingTelehealthGate);

  const supportedTabs = useMemo(
    () => getSupportedTabs({ isVirtualBackgroundSupported }),
    [isVirtualBackgroundSupported],
  );

  switch (drawerState) {
    case ProviderDrawerState.SETTINGS:
      return <SettingsDrawer tabMap={supportedTabs} />;
    case ProviderDrawerState.CLIENTS:
      return <ClientsDrawer />;
    case ProviderDrawerState.SESSION_CHAT:
    case ProviderDrawerState.WAITING_ROOM_CHAT:
    case ProviderDrawerState.PARTICIPANT_DISCONNECTED_CHAT:
      return <ChatDrawer />;
    case ProviderDrawerState.ALL_CLIENTS_CHAT_LIST:
      return <AllClientsChatDrawer />;
    case ProviderDrawerState.CLIENT_INFORMATION:
      return <ClientInformationDrawer />;
    case ProviderDrawerState.CLIENT_FORM_RESPONSE:
      return <ClientInformationFormResponseDrawer />;
    case ProviderDrawerState.TREATMENT_PLANS_CONTENT:
      return <TreatmentPlansContentDrawer />;
    case ProviderDrawerState.PROGRESS_NOTES:
      return <ClientInformationNotesDrawer />;
    case ProviderDrawerState.EDIT_APPOINTMENT:
      if (convertAppointmentDrawerToSeedlingTelehealth) {
        return <SchedulingAppointmentDrawerWrapper is_add_appointment={false} />;
      }
      return <EditAppointmentDrawer />;
    case ProviderDrawerState.PENDING_FORMS:
      return <PendingFormsDrawer />;
    case ProviderDrawerState.PENDING_FORMS_COMPLETED:
      return <PendingFormsCompletedDrawer />;
    case ProviderDrawerState.ADD_APPOINTMENT:
      if (convertAppointmentDrawerToSeedlingTelehealth) {
        return <SchedulingAppointmentDrawerWrapper is_add_appointment={true} />;
      }
      return <AddAppointmentDrawer />;
    case ProviderDrawerState.PENDING_MEASURES:
      return <PendingMeasuresDrawer />;
    case ProviderDrawerState.MEASURE_RESPONSES:
      return <MeasureResponsesDrawer />;
    case ProviderDrawerState.CSSRS_FORM:
      return <CSSRSForm />;
    default:
      return null;
  }
}

export function PushDrawerWrapper({ className, id }: Pick<PushDrawerProps, 'className' | 'id'>) {
  const { buttonRefs: openButtonRefs } = useDrawerButtonRefs();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [drawerState, setDrawerState] = useAtom(drawerStateAtom);
  const [currentConversationSid, setCurrentConversationSid] = useAtom(currentConversationSidAtom);
  const setSelectedMeasureBundle = useSetAtom(selectedBundleAtom);
  const setSelectedMeasureBundleItem = useSetAtom(selectedMeasureBundleItemIdAtom);
  const isVirtualBackgroundSupported = useAtomValue(isVirtualBackgroundSupportedAtom);

  const onCloseDrawer = useCallback((): void => {
    setDrawerState(null);
    setSelectedMeasureBundle(undefined);
    setSelectedMeasureBundleItem(undefined);
    if (currentConversationSid) {
      setCurrentConversationSid(undefined);
    }
    if (drawerState) {
      // On drawer close, try to refocus the button that would normally open
      // whatever `drawerState` was active at the time of closing.
      openButtonRefs.current?.get(drawerState)?.current?.focus();
    }
  }, [
    currentConversationSid,
    drawerState,
    openButtonRefs,
    setCurrentConversationSid,
    setDrawerState,
    setSelectedMeasureBundle,
    setSelectedMeasureBundleItem,
  ]);

  // On drawer state change, refocus the "close" button
  useEffect(() => {
    if (drawerState) {
      closeButtonRef.current?.focus();
    }
  }, [drawerState]);

  return (
    <PushDrawer
      id={id}
      isOpen={!!drawerState}
      onClose={onCloseDrawer}
      className={className}
      closeButtonRef={closeButtonRef}
    >
      <DrawerContent drawerState={drawerState} isVirtualBackgroundSupported={isVirtualBackgroundSupported} />
    </PushDrawer>
  );
}
